import { TimesheetEntryType } from '@sdk/timesheets/types/TimesheetEntry';
import { TimesheetEntryPayloadEntry } from '@sdk/timesheets/api/apiTypes';
import { AllBreakTypes, AutomatedBreakType } from '@sdk/settings/types';

import {
  TimeClockEntry,
  BreakEntry,
} from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.types';
import { getClockInClockOutDates } from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.utils';
import { getBreakSequenceData } from '@web/timesheets/EditTimesheetModal/utils/breakUtils';
import { formatDateToTimestampString } from '@web/timesheets/EditTimesheetModal/editTimesheetModal.utils';

type MapBreakProps = {
  timeClockEntry: TimeClockEntry;
  breakEntry: BreakEntry;
  type: TimesheetEntryType;
  start: Date;
  end: Date;
  userId: ID;
  shiftId: string;
};
const mapBreak = ({
  timeClockEntry,
  breakEntry,
  type,
  start,
  end,
  userId,
  shiftId,
}: MapBreakProps): TimesheetEntryPayloadEntry => {
  const { id, baseSequenceId, sequenceId } = getBreakSequenceData(
    type === TimesheetEntryType.breakStart ? breakEntry.startEntry : breakEntry.endEntry,
    timeClockEntry
  );

  return {
    id,
    baseSequenceId,
    sequenceId,
    timestamp: formatDateToTimestampString(type === TimesheetEntryType.breakStart ? start : end),
    type,
    user: {
      id: userId,
    },
    event: {
      id: shiftId,
    },
    automatedBreakType:
      breakEntry.type === AllBreakTypes.standard
        ? null
        : //
          (breakEntry.type as unknown as AutomatedBreakType),
    // If entire timeclock section got deleted it means we need to delete all of it's breaks too
    deleted: Boolean(timeClockEntry.deleted) || Boolean(breakEntry.deleted),
    replacesAttestedBreakNumber: breakEntry.replacesAttestedBreakNumber,
  };
};

type MapBreakEntryToTimesheetEntriesProps = {
  timeClockEntry: TimeClockEntry;
  breakEntry: BreakEntry;
  clockIn: Date;
  shift: {
    id: string;
    user: {
      id: number;
    };
  };
};

/**
 * Methods maps single break entry to one or two timesheet entries (one per break_start and break_end).
 * It takes into consideration the entire Time Clock section - as it can affect deleted flag of particular break.
 */
const mapBreakEntryToTimesheetEntries = ({
  timeClockEntry,
  breakEntry,
  clockIn,
  shift,
}: MapBreakEntryToTimesheetEntriesProps): TimesheetEntryPayloadEntry[] => {
  const timesheetEntries: TimesheetEntryPayloadEntry[] = [];
  const { clockIn: start, clockOut: end } = getClockInClockOutDates(
    clockIn,
    breakEntry.start,
    breakEntry.end,
    { isDateWithTime: true }
  );

  if (breakEntry.start) {
    timesheetEntries.push(
      mapBreak({
        timeClockEntry,
        breakEntry,
        type: TimesheetEntryType.breakStart,
        start,
        end,
        userId: shift.user.id,
        shiftId: shift.id,
      })
    );
  }

  if (breakEntry.end) {
    timesheetEntries.push(
      mapBreak({
        timeClockEntry,
        breakEntry,
        type: TimesheetEntryType.breakEnd,
        start,
        end,
        userId: shift.user.id,
        shiftId: shift.id,
      })
    );
  }

  return timesheetEntries;
};

export default mapBreakEntryToTimesheetEntries;
